"use client"

import { useEffect, useState } from 'react'
import { Info, AlertTriangle, PartyPopper, X } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'

/**
 * Platform-wide announcement strip under the header. Shows the newest
 * ACTIVE announcement written by platform admins in the god console.
 * Dismissal is per-announcement, per-browser (localStorage) — a new
 * announcement shows up even if an older one was dismissed.
 * Fails silently offline: no announcement is not an error state.
 */

type Ann = { id: string; message: string; type: 'info' | 'warning' | 'success' }

const DISMISS_KEY = 'dismissed_announcement_id'

const STYLES: Record<Ann['type'], { cls: string; Icon: typeof Info }> = {
  info: {
    cls: 'bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/25 text-indigo-700 dark:text-indigo-300',
    Icon: Info,
  },
  warning: {
    cls: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/25 text-amber-700 dark:text-amber-300',
    Icon: AlertTriangle,
  },
  success: {
    cls: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/25 text-emerald-700 dark:text-emerald-300',
    Icon: PartyPopper,
  },
}

export function AnnouncementBanner() {
  const t = useT()
  const [ann, setAnn] = useState<Ann | null>(null)
  const [dismissed, setDismissed] = useState<string | null>(null)

  useEffect(() => {
    try { setDismissed(localStorage.getItem(DISMISS_KEY)) } catch { /* private mode */ }
    const supabase = createClient()
    ;(async () => {
      const { data, error } = await supabase
        .from('platform_announcements')
        .select('id, message, type')
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(1)
      if (!error && data?.[0]) setAnn(data[0] as Ann)
    })().catch(() => { /* offline: no banner, no error */ })
  }, [])

  if (!ann || ann.id === dismissed) return null

  const { cls, Icon } = STYLES[ann.type] ?? STYLES.info

  const dismiss = () => {
    setDismissed(ann.id)
    try { localStorage.setItem(DISMISS_KEY, ann.id) } catch { /* private mode */ }
  }

  return (
    <div className={cn('shrink-0 rounded-2xl border px-4 py-2 flex items-center gap-2.5 text-xs font-bold', cls)}>
      <Icon className="size-4 shrink-0" />
      <span className="flex-1 min-w-0">{ann.message}</span>
      <button
        onClick={dismiss}
        aria-label={t('set.announce.1')}
        className="shrink-0 size-7 rounded-lg flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}
